import { Commands } from './Commands';
import { AktiveBingoaufgaben } from '../components/AktiveBingoaufgaben';
import { useState } from 'react';
import { WinPunktetafel } from './WinPunktetafel';
import { Ansagen } from '../components/Ansagen';
import { BingoBoard } from '../components/BingoBoard';
import { Punktestand } from '../components/Punktestand';
import { Zeitanzeige } from '../components/Zeitanzeige';
import { api, ApiError } from '../lib/api';
import { Challenges } from './Challenges';
import { Einrichtung } from './Einrichtung';
import { Probe } from './Probe';
import { useLive } from '../lib/live';
import type { OwnerState } from '../lib/types';

/**
 * Die Verwaltungsansicht.
 *
 * Zwei Lagen: Oben steht alles, was während des Abends gebraucht wird – Runde starten, Timer
 * bedienen, Punkte sehen, die OBS-Adressen kopieren. Die Einrichtung ist eingeklappt, weil sie
 * meist vor dem Stream einmal gemacht und danach nicht mehr angefasst wird.
 *
 * Das verschiebbare Modulraster kommt später; es ändert nichts an den Diensten dahinter.
 */
export function OwnerView({ lobbyId, onLogout }: { lobbyId: string; onLogout: () => void }) {
  const live = useLive<OwnerState>({ lobbyId });
  const [meldung, setMeldung] = useState<string | null>(null);
  const [zeigeEinrichtung, setZeigeEinrichtung] = useState(false);
  const [boardTeamId, setBoardTeamId] = useState('');
  const [bereich, setBereich] = useState<'punkte' | 'bingo' | 'challenges' | 'commands'>('punkte');

  if (live.fehler) {
    return (
      <main className="page">
        <p className="notice">{live.fehler}</p>
        <button type="button" onClick={onLogout}>
          Abmelden
        </button>
      </main>
    );
  }
  if (!live.state) {
    return (
      <main className="page">
        <p className="muted">verbindet …</p>
      </main>
    );
  }

  const { lobby, teams, boards, scores, timer, taskCount, members } = live.state;

  async function tun(was: () => Promise<unknown>, erfolg: string) {
    setMeldung(null);
    try {
      await was();
      setMeldung(erfolg);
    } catch (err) {
      setMeldung(err instanceof ApiError ? err.message : 'Das hat nicht geklappt.');
    }
  }

  const basis = window.location.origin;

  return (
    <main className="page owner">
      <header className="page-head">
        <div>
          <p className="muted small">
            {lobby.name}
            {lobby.round ? ` · Runde ${lobby.round}` : ' · noch keine Runde'}
            {live.status !== 'live' ? ' · Verbindung unterbrochen' : ''}
          </p>
          <h1>Regiepult</h1>
        </div>
        <div className="row">
          <button type="button" onClick={() => setZeigeEinrichtung((z) => !z)}>
            {zeigeEinrichtung ? 'Einrichtung schließen' : 'Einrichten'}
          </button>
          <button type="button" className="ghost" onClick={onLogout}>
            Abmelden
          </button>
        </div>
      </header>

      {zeigeEinrichtung && (
        <Einrichtung state={live.state} onFertig={() => setZeigeEinrichtung(false)} />
      )}

      <nav className="row" aria-label="Win-Challenge Bereiche" style={{ marginBottom: '1rem' }}>
        <button type="button" className={bereich === 'punkte' ? 'primary' : 'ghost'} onClick={() => setBereich('punkte')}>Punktetafel</button>
        <button type="button" className={bereich === 'bingo' ? 'primary' : 'ghost'} onClick={() => setBereich('bingo')}>Bingo + Extrapunkte</button>
        <button type="button" className={bereich === 'challenges' ? 'primary' : 'ghost'} onClick={() => setBereich('challenges')}>Challenge-Umfragen</button>
        <button type="button" className={bereich === 'commands' ? 'primary' : 'ghost'} onClick={() => setBereich('commands')}>Commands</button>
      </nav>
      {bereich === 'punkte' && <WinPunktetafel lobbyId={lobbyId} />}
      {bereich === 'bingo' && <Zeitanzeige timer={timer} />}

      {bereich === 'bingo' && <section className="panel">
        <h2>Ablauf</h2>
        <div className="row">
          <button
            type="button"
            className="primary"
            onClick={() => void tun(() => api.startRound(lobby.id), 'Neue Runde erzeugt.')}
          >
            Neue Runde
          </button>
          {lobby.round !== null && (
            <button type="button" className="ghost" onClick={() => {
              if (!window.confirm('Diese Runde wirklich zurücksetzen? Alle Markierungen und Punkte dieser Runde gehen verloren. Frühere Rundenergebnisse bleiben erhalten.')) return;
              void tun(() => api.resetRound(lobby.id), 'Runde zurückgesetzt. Du kannst sie erneut starten.');
            }}>
              Runde zurücksetzen
            </button>
          )}
          {timer.phase === 'IDLE' && !timer.preparationEndsAt && lobby.round !== null && (
            <button type="button" onClick={() => void tun(() => api.timer(lobby.id, 'START'), 'Gestartet.')}>
              Start
            </button>
          )}
          {timer.phase === 'RUNNING' && (
            <button type="button" onClick={() => void tun(() => api.timer(lobby.id, 'PAUSE'), 'Pausiert.')}>
              Pause
            </button>
          )}
          {timer.phase === 'PAUSED' && (
            <button type="button" onClick={() => void tun(() => api.timer(lobby.id, 'RESUME'), 'Läuft weiter.')}>
              Fortsetzen
            </button>
          )}
          {timer.phase !== 'ENDED' && timer.phase !== 'IDLE' && (
            <button type="button" onClick={() => void tun(() => api.timer(lobby.id, 'END'), 'Runde beendet.')}>
              Beenden
            </button>
          )}
        </div>
        <div className="row" style={{ marginTop: '0.6rem' }}>
          {[5, 10, 15].map((minuten) => (
            <button
              key={minuten}
              type="button"
              className="ghost"
              onClick={() => void tun(() => api.timer(lobby.id, 'BONUS', minuten), `+${minuten} Minuten.`)}
            >
              +{minuten} Min
            </button>
          ))}
        </div>
        {meldung && <p className="muted small">{meldung}</p>}
      </section>}

      {bereich === 'bingo' && <section className="panel">
        <h2>Bingo-Punkte</h2>
        <Punktestand teams={teams} scores={scores} />
      </section>}

      {bereich === 'challenges' && <Challenges state={live.state} />}
      {bereich === 'commands' && <Commands lobbyId={lobbyId} />}

      <section className="panel">
        <h2>Für OBS</h2>
        <p className="muted small">
          Diese Adressen in OBS als Browserquelle eintragen. Sie brauchen keine Anmeldung – wer den
          Schlüssel hat, sieht die Ansicht. Über „Neuer Schlüssel“ lassen sie sich widerrufen.
        </p>
        <ul className="links">
          <li>
            <span>Bingo</span>
            <code>{`${basis}/overlay/${lobby.overlayKey}`}</code>
          </li>
          <li>
            <span>Punkte (waagerecht)</span>
            <code>{`${basis}/overlay/${lobby.overlayKey}/scores`}</code>
          </li>
          <li>
            <span>Punkte (senkrecht)</span>
            <code>{`${basis}/overlay/${lobby.overlayKey}/scores?layout=vertikal`}</code>
          </li>
          <li>
            <span>Challenges</span>
            <code>{`${basis}/overlay/${lobby.overlayKey}/challenges`}</code>
          </li>
          {lobby.boardMode === 'INDIVIDUAL' &&
            teams.map((team) => (
              <li key={team.id}>
                <span>Bingo: {team.name}</span>
                <code>{`${basis}/overlay/${lobby.overlayKey}?team=${team.id}`}</code>
              </li>
            ))}
        </ul>
      </section>

      <section className="panel">
        <h2>Überblick</h2>
        <p className="muted small">
          {teams.length} Team(s) · {taskCount.active} von {taskCount.total} Aufgaben aktiv ·{' '}
          {members.length} Person(en) auf der Whitelist
        </p>
        {taskCount.active < lobby.boardSize * lobby.boardSize && (
          <p className="notice">
            Für ein {lobby.boardSize}×{lobby.boardSize}-Board werden{' '}
            {lobby.boardSize * lobby.boardSize} aktive Aufgaben gebraucht. Mit{' '}
            {taskCount.active} lässt sich keine Runde starten.
          </p>
        )}
      </section>

      <Probe state={live.state} />

      <Ansagen stand={{ timer, scores, teams, poll: live.state.poll }} />

      {bereich === 'bingo' && <section className="panel">
        <h2>Bingo-Board steuern</h2>
        <label>Team auswählen
          <select value={teams.some((team) => team.id === boardTeamId) ? boardTeamId : (teams[0]?.id ?? '')}
            onChange={(event) => setBoardTeamId(event.target.value)}>
            {teams.map((team) => <option key={team.id} value={team.id}>{team.name}</option>)}
          </select>
        </label>
        <p className="muted small">Ein Feld anklicken, um es für das gewählte Team zu markieren oder die Markierung zurückzunehmen. Punkte und Bingos werden wie im Spielerboard berechnet.</p>
        {(() => {
          const teamId = teams.some((team) => team.id === boardTeamId) ? boardTeamId : teams[0]?.id;
          const board = boards.find((b) => b.teamId === teamId) ?? boards.find((b) => b.teamId === null);
          if (!board) return <p className="muted">Noch kein Bingo-Board vorhanden.</p>;
          return <div className="wc-bingo-mit-liste">
            <div className="wc-bingo-hauptbereich"><BingoBoard board={board} teams={teams} scores={scores}
              istAnklickbar={timer.phase === 'RUNNING' && !!teamId}
              onToggle={(cellIndex) => {
                if (teamId) void tun(() => api.toggleField(lobby.id, teamId, cellIndex), 'Feld aktualisiert.');
              }} /></div>
            <aside className="wc-bingo-rechts"><AktiveBingoaufgaben board={board} teamId={teamId} teams={teams} /></aside>
          </div>;
        })()}
      </section>}

    </main>
  );
}
